import { useState } from "react";
import {
  Badge,
  Button,
  Empty,
  Modal,
  Pagination,
  Popover,
  Spin,
  message,
} from "antd";
import {
  BellOutlined,
  MessageOutlined,
  PhoneOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { useHhRpmNotifications } from "./useHhRpmNotifications";
import styles from "./HhRpmNotificationBell.module.scss";

dayjs.extend(utc);
dayjs.extend(timezone);

interface NotificationSelection {
  patient: IHhRpmCommunicationPatient;
  episode: IHhRpmNotificationEpisode;
}

const formatPhone = (value: string) => {
  const digits = value.replace(/\D/g, "").slice(-10);
  return digits.length === 10
    ? `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`
    : value;
};

const latestCallSummary = (
  call: ICompassusPatientCallLog | null | undefined
): string | null => {
  if (!call) return null;
  if (call.status === "ringing" || call.status === "in-progress") {
    return "Call in progress";
  }
  if (call.duration_seconds == null) return "Call ended";
  const minutes = Math.floor(call.duration_seconds / 60);
  const seconds = call.duration_seconds % 60;
  const duration = minutes ? `${minutes}m ${seconds}s` : `${seconds}s`;
  return `Latest call · ${duration}`;
};

const patientNames = (patients: IHhRpmCommunicationPatient[]) => {
  const names = patients
    .filter((patient) => patient.active)
    .map((patient) => patient.patient_name);
  if (names.length <= 2) return names.join(", ") || "Unknown patient";
  return `${names.slice(0, 2).join(", ")} +${names.length - 2}`;
};

export const HhRpmNotificationBell = ({
  clinicId,
  clinicTimezone,
  onSelectPatient,
}: {
  clinicId: number;
  clinicTimezone?: string | null;
  onSelectPatient: (selection: NotificationSelection) => void;
}) => {
  const [open, setOpen] = useState(false);
  const [page, setPage] = useState(1);
  const [chooserEpisode, setChooserEpisode] =
    useState<IHhRpmNotificationEpisode | null>(null);
  const chooserPatients =
    chooserEpisode?.patients.filter((patient) => patient.active) ?? [];
  const showChooserMetadata = chooserPatients.some(
    (patient) => patient.clinic_id != null && patient.clinic_id !== clinicId
  );
  const { count, episodes, total, isLoading, error } = useHhRpmNotifications({
    clinicId,
    enabled: clinicId > 0,
    listEnabled: open,
    page,
    pageSize: 20,
  });

  const selectEpisode = (episode: IHhRpmNotificationEpisode) => {
    const patients = episode.patients.filter((patient) => patient.active);
    const restrictedPatientCount = episode.restricted_patient_count ?? 0;
    if (patients.length === 1 && restrictedPatientCount === 0) {
      setOpen(false);
      onSelectPatient({ patient: patients[0], episode });
      return;
    }
    if (patients.length > 0 || restrictedPatientCount > 0) {
      setChooserEpisode(episode);
      return;
    }
    message.warning("No active patient is linked to this phone number.");
  };

  const content = (
    <div className={styles.panel}>
      <div className={styles.panelHeader}>
        <strong>Notifications</strong>
        <span>{count} unread</span>
      </div>
      <div className={styles.list}>
        {isLoading ? (
          <div className={styles.centered}>
            <Spin size="small" />
          </div>
        ) : error ? (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description="Unable to load notifications."
          />
        ) : episodes.length === 0 ? (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description="No notifications yet."
          />
        ) : (
          episodes.map((episode) => {
            const isNew = episode.status === "new";
            const latestAt =
              episode.latest_message?.created_at ??
              episode.latest_call?.started_at ??
              episode.last_activity_at;
            const eventCount = episode.event_count;
            const isCall = episode.notification_type === "call";
            const callSummary = latestCallSummary(episode.latest_call);
            return (
              <button
                type="button"
                className={`${styles.notification} ${
                  isNew ? styles.notificationNew : ""
                }`}
                key={episode.episode_id}
                onClick={() => selectEpisode(episode)}
              >
                <span
                  className={`${styles.unreadDot} ${
                    isNew ? "" : styles.unreadDotHidden
                  }`}
                  aria-label={
                    isNew
                      ? isCall
                        ? "Unread calls"
                        : "Unread messages"
                      : undefined
                  }
                />
                <div className={styles.avatar}>
                  {isCall ? <PhoneOutlined /> : <MessageOutlined />}
                </div>
                <div className={styles.notificationBody}>
                  <div>
                    <strong>{patientNames(episode.patients)}</strong>
                    {latestAt ? (
                      <time>
                        {dayjs
                          .utc(latestAt)
                          .tz(clinicTimezone || "UTC")
                          .format("MMM D, h:mm A")}
                      </time>
                    ) : null}
                  </div>
                  <span>{formatPhone(episode.phone_number)}</span>
                  {isNew ? (
                    <p>
                      {isCall ? "has made" : "has sent"} {eventCount} new{" "}
                      {isCall
                        ? eventCount === 1
                          ? "call attempt"
                          : "call attempts"
                        : eventCount === 1
                        ? "message"
                        : "messages"}
                    </p>
                  ) : (
                    <p className={styles.reviewedLabel}>
                      {eventCount}{" "}
                      {isCall
                        ? eventCount === 1
                          ? "call attempt"
                          : "call attempts"
                        : eventCount === 1
                        ? "message"
                        : "messages"}{" "}
                      · Reviewed
                    </p>
                  )}
                  {episode.latest_message?.body ? (
                    <small>{episode.latest_message.body}</small>
                  ) : callSummary ? (
                    <small>{callSummary}</small>
                  ) : null}
                </div>
              </button>
            );
          })
        )}
      </div>
      {total > 20 ? (
        <Pagination
          className={styles.pagination}
          current={page}
          pageSize={20}
          total={total}
          showSizeChanger={false}
          size="small"
          onChange={setPage}
        />
      ) : null}
    </div>
  );

  return (
    <>
      <Popover
        content={content}
        trigger="click"
        open={open}
        onOpenChange={(nextOpen) => {
          setOpen(nextOpen);
          if (nextOpen) setPage(1);
        }}
        placement="bottomRight"
      >
        <Badge count={count} size="small" overflowCount={99}>
          <Button
            shape="circle"
            icon={<BellOutlined />}
            aria-label="Communication notifications"
            title="Communication notifications"
          />
        </Badge>
      </Popover>
      <Modal
        open={chooserEpisode !== null}
        title="Choose a patient"
        footer={null}
        onCancel={() => setChooserEpisode(null)}
      >
        <p className={styles.chooserDescription}>
          This phone number is linked to multiple patient records. Choose the
          patient whose Communication tab you want to open.
        </p>
        <div className={styles.patientChoices}>
          {chooserPatients.map((patient) => (
            <Button
              key={`${patient.clinic_id ?? "unknown"}:${patient.patient_id}`}
              block
              className={styles.patientChoice}
              aria-label={
                showChooserMetadata
                  ? [
                      patient.patient_name,
                      patient.clinic_name,
                      patient.mr_no ? `MRN ${patient.mr_no}` : null,
                    ]
                      .filter(Boolean)
                      .join(", ")
                  : patient.patient_name
              }
              onClick={() => {
                const episode = chooserEpisode;
                if (!episode) return;
                setChooserEpisode(null);
                setOpen(false);
                onSelectPatient({ patient, episode });
              }}
            >
              <span className={styles.patientChoiceContent}>
                <strong>{patient.patient_name}</strong>
                {showChooserMetadata ? (
                  <span className={styles.patientChoiceMeta}>
                    {patient.clinic_name}
                    {patient.mr_no ? ` · MRN ${patient.mr_no}` : ""}
                  </span>
                ) : null}
              </span>
            </Button>
          ))}
          {(chooserEpisode?.restricted_patient_count ?? 0) > 0 ? (
            <Button block disabled>
              {chooserEpisode?.restricted_patient_count} restricted{" "}
              {chooserEpisode?.restricted_patient_count === 1
                ? "patient"
                : "patients"}{" "}
              from another clinic
            </Button>
          ) : null}
        </div>
      </Modal>
    </>
  );
};
